import React, { useState } from 'react'
import {
  BookOpen,
  TrendingUp,
  Shield,
  Target,
  AlertTriangle,
  ChevronRight,
  ExternalLink,
  Award,
  BarChart3
} from 'lucide-react'
import { Button } from '@/components/ui/Button'

export function EducationalRiskContent() {
  const [activeSection, setActiveSection] = useState<'glossary' | 'best_practices' | 'benchmarks' | 'case_studies'>('glossary')

  const riskGlossary = [
    {
      term: 'Concentration Risk',
      definition: 'The risk arising from lack of diversification across geographic locations, property types, or tenant types.',
      example: 'Having 80% of your portfolio in one city creates high geographic concentration risk.'
    },
    {
      term: 'Liquidity Risk',
      definition: 'The risk of not being able to sell a property quickly at fair market value when needed.',
      example: 'Commercial properties in small towns typically have higher liquidity risk than residential properties in major cities.'
    },
    {
      term: 'Leverage Risk',
      definition: 'The increased risk that comes with using debt financing to purchase properties.',
      example: 'A 90% LTV loan creates more leverage risk than a 70% LTV loan due to higher debt service requirements.'
    },
    {
      term: 'Vacancy Risk',
      definition: 'The risk of properties remaining unoccupied, resulting in lost rental income.',
      example: 'Student housing near universities may have higher vacancy risk during economic downturns.'
    },
    {
      term: 'Interest Rate Risk',
      definition: 'The risk that changes in interest rates will negatively affect property values or cash flows.',
      example: 'Variable rate mortgages expose investors to interest rate risk if rates increase significantly.'
    },
    {
      term: 'Market Risk',
      definition: 'The risk that local or national real estate market conditions will negatively impact property performance.',
      example: 'Economic recession causing widespread job losses in a region creates significant market risk.'
    },
    {
      term: 'Sharpe Ratio',
      definition: 'A measure of risk-adjusted return calculated as (portfolio return - risk-free rate) / portfolio volatility.',
      example: 'A Sharpe ratio of 1.5 means you earn 1.5% excess return for each 1% of volatility taken.'
    },
    {
      term: 'Value at Risk (VaR)',
      definition: 'The maximum expected loss over a specific time period at a given confidence level.',
      example: 'A 95% VaR of $100,000 means there\'s a 5% chance of losing more than $100,000 in the specified period.'
    }
  ]

  const bestPractices = [
    {
      category: 'Geographic Diversification',
      title: 'Spread Investments Across Markets',
      description: 'Reduce concentration risk by investing in multiple geographic markets with different economic drivers.',
      implementation_steps: [
        'Limit exposure to any single metropolitan area to 20-25% of portfolio value',
        'Research markets with different economic bases (tech, manufacturing, government, etc.)',
        'Consider population growth trends and employment diversification',
        'Monitor local market indicators and economic forecasts',
        'Gradually expand to new markets as portfolio grows'
      ]
    },
    {
      category: 'Property Type Diversification',
      title: 'Mix Different Property Types',
      description: 'Balance your portfolio across residential, commercial, and other property types to reduce sector-specific risks.',
      implementation_steps: [
        'Start with familiar property types (typically single-family residential)',
        'Gradually add multi-family, commercial, or industrial properties',
        'Understand the unique risks and management requirements of each type',
        'Consider REITs for exposure to property types outside your expertise',
        'Monitor performance across different property types'
      ]
    },
    {
      category: 'Leverage Management',
      title: 'Maintain Conservative Debt Levels',
      description: 'Use leverage strategically while maintaining ability to service debt through market cycles.',
      implementation_steps: [
        'Target portfolio-wide LTV ratio below 70-75%',
        'Maintain adequate cash reserves (6-12 months of expenses)',
        'Consider fixed-rate mortgages to reduce interest rate risk',
        'Stress test debt service coverage under various scenarios',
        'Plan for refinancing well before loan maturity dates'
      ]
    },
    {
      category: 'Cash Flow Management',
      title: 'Build Robust Cash Flow Models',
      description: 'Develop comprehensive cash flow projections that account for various risk scenarios.',
      implementation_steps: [
        'Model conservative vacancy rates based on local market data',
        'Include adequate reserves for maintenance and capital expenditures',
        'Stress test cash flows under different interest rate scenarios',
        'Plan for rent growth and expense inflation',
        'Maintain emergency funds for unexpected expenses'
      ]
    },
    {
      category: 'Risk Monitoring',
      title: 'Implement Regular Risk Assessment',
      description: 'Continuously monitor and assess portfolio risks to make proactive adjustments.',
      implementation_steps: [
        'Conduct quarterly portfolio risk reviews',
        'Track key performance indicators (KPIs) for each property',
        'Monitor local market conditions and economic indicators',
        'Review and update insurance coverage annually',
        'Maintain detailed records for tax and performance analysis'
      ]
    }
  ]

  const industryBenchmarks = [
    {
      metric: 'Portfolio Geographic Concentration',
      industry_average: 35,
      top_quartile: 20,
      your_value: 45,
      interpretation: 'Your concentration is above industry average. Consider expanding to new markets.'
    },
    {
      metric: 'Loan-to-Value Ratio (%)',
      industry_average: 65,
      top_quartile: 55,
      your_value: 72,
      interpretation: 'Your leverage is above average. Consider paying down debt or adding equity.'
    },
    {
      metric: 'Vacancy Rate (%)',
      industry_average: 8,
      top_quartile: 5,
      your_value: 6,
      interpretation: 'Excellent vacancy management. You\'re in the top quartile of investors.'
    },
    {
      metric: 'Annual Return (%)',
      industry_average: 12,
      top_quartile: 18,
      your_value: 15,
      interpretation: 'Above-average returns. Consider if you\'re taking appropriate risk for these returns.'
    },
    {
      metric: 'Cash-on-Cash Return (%)',
      industry_average: 8,
      top_quartile: 12,
      your_value: 10,
      interpretation: 'Good cash-on-cash returns, approaching top quartile performance.'
    },
    {
      metric: 'Debt Service Coverage Ratio',
      industry_average: 1.25,
      top_quartile: 1.5,
      your_value: 1.35,
      interpretation: 'Healthy debt coverage with good buffer for market fluctuations.'
    }
  ]

  const caseStudies = [
    {
      title: '2008 Financial Crisis: Leverage Lessons',
      scenario: 'Investor with 90% LTV across 10 properties in Las Vegas',
      outcome: 'Lost entire portfolio due to inability to service debt when values dropped 60%',
      lessons: ['High leverage amplifies losses', 'Geographic concentration compounds risk', 'Cash reserves are critical']
    },
    {
      title: 'COVID-19 Pandemic: Tenant Risk',
      scenario: 'Portfolio concentrated in retail and hospitality properties',
      outcome: 'Massive vacancy increases and rent collection issues',
      lessons: ['Diversify across recession-resistant property types', 'Screen tenants carefully', 'Maintain larger emergency funds']
    },
    {
      title: 'Rising Interest Rates 2022-2023',
      scenario: 'Variable rate mortgages with interest-only periods',
      outcome: 'Cash flow turned negative as rates doubled',
      lessons: ['Fixed-rate debt provides predictability', 'Stress test for interest rate changes', 'Plan for rate resets']
    }
  ]

  const formatPercentage = (value: number) => `${value}%`

  const getBenchmarkColor = (your_value: number, top_quartile: number, industry_average: number, isLowerBetter = false) => {
    if (isLowerBetter) {
      if (your_value <= top_quartile) return 'text-green-600'
      if (your_value <= industry_average) return 'text-yellow-600'
      return 'text-red-600'
    } else {
      if (your_value >= top_quartile) return 'text-green-600'
      if (your_value >= industry_average) return 'text-yellow-600'
      return 'text-red-600'
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Risk Education Center</h3>
        <p className="text-gray-600">
          Learn about real estate investment risks and best practices for portfolio management
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'glossary', name: 'Risk Glossary', icon: BookOpen },
            { id: 'best_practices', name: 'Best Practices', icon: Target },
            { id: 'benchmarks', name: 'Industry Benchmarks', icon: BarChart3 },
            { id: 'case_studies', name: 'Case Studies', icon: Award }
          ].map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id as any)}
                className={`
                  flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm
                  ${activeSection === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.name}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Content Sections */}
      <div>
        {activeSection === 'glossary' && (
          <div className="space-y-6">
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
              <div className="flex items-center space-x-2 mb-2">
                <BookOpen className="w-5 h-5 text-blue-600" />
                <h4 className="text-lg font-semibold text-blue-900">Risk Management Glossary</h4>
              </div>
              <p className="text-blue-800">
                Understanding key risk concepts is essential for making informed investment decisions.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {riskGlossary.map((item, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                  <h5 className="font-semibold text-gray-900 mb-2">{item.term}</h5>
                  <p className="text-gray-700 text-sm mb-3">{item.definition}</p>
                  {item.example && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Example</span>
                      <p className="text-sm text-gray-700 mt-1">{item.example}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeSection === 'best_practices' && (
          <div className="space-y-6">
            <div className="bg-green-50 rounded-lg border border-green-200 p-6">
              <div className="flex items-center space-x-2 mb-2">
                <Target className="w-5 h-5 text-green-600" />
                <h4 className="text-lg font-semibold text-green-900">Risk Management Best Practices</h4>
              </div>
              <p className="text-green-800">
                Proven strategies for building a resilient real estate investment portfolio.
              </p>
            </div>

            <div className="space-y-6">
              {bestPractices.map((practice, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="inline-block px-3 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full mb-2">
                        {practice.category}
                      </span>
                      <h5 className="font-semibold text-gray-900">{practice.title}</h5>
                      <p className="text-gray-700 text-sm mt-1">{practice.description}</p>
                    </div>
                    <Shield className="w-5 h-5 text-green-600 flex-shrink-0" />
                  </div>

                  <div>
                    <h6 className="font-medium text-gray-900 mb-2">Implementation Steps:</h6>
                    <ul className="space-y-2">
                      {practice.implementation_steps.map((step, stepIndex) => (
                        <li key={stepIndex} className="flex items-start space-x-2">
                          <ChevronRight className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeSection === 'benchmarks' && (
          <div className="space-y-6">
            <div className="bg-purple-50 rounded-lg border border-purple-200 p-6">
              <div className="flex items-center space-x-2 mb-2">
                <BarChart3 className="w-5 h-5 text-purple-600" />
                <h4 className="text-lg font-semibold text-purple-900">Industry Benchmarks</h4>
              </div>
              <p className="text-purple-800">
                Compare your portfolio performance against industry standards and top performers.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {industryBenchmarks.map((benchmark, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                  <h5 className="font-semibold text-gray-900 mb-4">{benchmark.metric}</h5>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Your Value</span>
                      <span className={`font-bold ${getBenchmarkColor(
                        benchmark.your_value,
                        benchmark.top_quartile,
                        benchmark.industry_average,
                        benchmark.metric.includes('Concentration') || benchmark.metric.includes('Vacancy') || benchmark.metric.includes('LTV')
                      )}`}>
                        {benchmark.metric.includes('Ratio') ? benchmark.your_value.toFixed(2) : formatPercentage(benchmark.your_value)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Industry Average</span>
                      <span className="font-medium text-gray-700">
                        {benchmark.metric.includes('Ratio') ? benchmark.industry_average.toFixed(2) : formatPercentage(benchmark.industry_average)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Top Quartile</span>
                      <span className="font-medium text-green-600">
                        {benchmark.metric.includes('Ratio') ? benchmark.top_quartile.toFixed(2) : formatPercentage(benchmark.top_quartile)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">{benchmark.interpretation}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeSection === 'case_studies' && (
          <div className="space-y-6">
            <div className="bg-orange-50 rounded-lg border border-orange-200 p-6">
              <div className="flex items-center space-x-2 mb-2">
                <Award className="w-5 h-5 text-orange-600" />
                <h4 className="text-lg font-semibold text-orange-900">Real-World Case Studies</h4>
              </div>
              <p className="text-orange-800">
                Learn from historical examples of how different risk scenarios have played out for real estate investors.
              </p>
            </div>

            <div className="space-y-6">
              {caseStudies.map((study, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                  <h5 className="font-semibold text-gray-900 mb-3">{study.title}</h5>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h6 className="font-medium text-gray-700 mb-2">Scenario:</h6>
                      <p className="text-sm text-gray-600 mb-4">{study.scenario}</p>

                      <h6 className="font-medium text-gray-700 mb-2">Outcome:</h6>
                      <p className="text-sm text-red-600">{study.outcome}</p>
                    </div>

                    <div>
                      <h6 className="font-medium text-gray-700 mb-2">Key Lessons:</h6>
                      <ul className="space-y-2">
                        {study.lessons.map((lesson, lessonIndex) => (
                          <li key={lessonIndex} className="flex items-start space-x-2">
                            <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{lesson}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
              <div className="flex items-start space-x-3">
                <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Additional Resources</h4>
                  <div className="space-y-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Real Estate Risk Management Guide
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Portfolio Diversification Strategies
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Market Cycle Analysis
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}